/**
 * Insights API service for the Portfolio Insights page.
 *
 * - `generateInsights` POSTs to the Insights Lambda Function URL and processes
 *   the SSE response (`activity`, `insights_report`, `done`, `error`), using the
 *   same fetch + ReadableStream + line-parser idiom as `chatApi.ts`. It works
 *   with both a live-streamed body and a fully buffered body.
 * - `fetchLatestInsights` GETs the most recent stored report for an account.
 *
 * Both functions only talk to an `https://` Function URL (Requirement 11.7) and
 * give up when no response completes within 300 seconds (Requirement 2.6).
 *
 * Error messages passed to callers are generic and user-safe. The client never
 * reads an HTTP error body, and it surfaces only the sanitized `message` of an
 * SSE `error` event (Requirement 10.5).
 */

import type { Activity, ActivityStatus } from '@/types/chat';
import type { InsightsReport, LatestInsightsResponse } from '@/types/insights';

/** Client-side no-response timeout: matches the 300 s backend ceiling (Req 2.6). */
export const INSIGHTS_TIMEOUT_MS = 300_000;

/** Longest SSE `error` message the client will surface. */
const MAX_SERVER_MESSAGE_LENGTH = 200;

const VALID_ACTIVITY_STATUSES: readonly string[] = ['started', 'completed', 'failed'];

/** Category of a client-visible Insights failure. */
export type InsightsErrorKind =
  | 'config'
  | 'invalid_request'
  | 'auth'
  | 'forbidden'
  | 'busy'
  | 'timeout'
  | 'failed';

/** User-safe error reported by the Insights API service. */
export class InsightsError extends Error {
  constructor(
    message: string,
    public kind: InsightsErrorKind
  ) {
    super(message);
    this.name = 'InsightsError';
  }
}

const MESSAGES = {
  config: 'Portfolio Insights is not configured correctly. Please contact support.',
  invalidRequest: 'Select an account before requesting insights.',
  auth: 'Your session has expired. Please sign in again.',
  forbidden: 'You are not authorized to view insights for this account.',
  busy: 'The service is temporarily busy. Please try again shortly.',
  timeout: 'Insights generation timed out. Please try again.',
  failed: 'Insights generation did not complete. Please try again.',
  loadFailed: 'Unable to load the latest insights. Please try again.',
} as const;

/** Callbacks for `generateInsights`. */
export interface GenerateInsightsHandlers {
  /** Invoked for each well-formed provenance/progress activity event. */
  onActivity?: (activity: Activity) => void;
  /** Invoked once with the completed report, immediately before `onComplete`. */
  onReport: (report: InsightsReport) => void;
  /** Invoked once when generation finishes successfully. */
  onComplete: () => void;
  /** Invoked once when generation fails. Not invoked when the caller aborts. */
  onError: (error: InsightsError) => void;
  /** Aborts the request. Aborting is not reported as an error. */
  signal?: AbortSignal;
}

/**
 * Read the Insights Function URL, refusing anything that is not https.
 * Read at call time so configuration problems surface as a normal error.
 */
function resolveInsightsUrl(): string {
  const raw: unknown = import.meta.env.VITE_INSIGHTS_URL;
  if (typeof raw !== 'string' || !raw.trim()) {
    throw new InsightsError(MESSAGES.config, 'config');
  }
  const value = raw.trim();
  let protocol: string;
  try {
    protocol = new URL(value).protocol;
  } catch {
    throw new InsightsError(MESSAGES.config, 'config');
  }
  if (protocol !== 'https:') {
    throw new InsightsError(MESSAGES.config, 'config');
  }
  return value;
}

/** Map an HTTP error status to a generic user-safe error. The body is never read. */
function errorForStatus(status: number): InsightsError {
  if (status === 401) return new InsightsError(MESSAGES.auth, 'auth');
  if (status === 403) return new InsightsError(MESSAGES.forbidden, 'forbidden');
  if (status === 400) return new InsightsError(MESSAGES.invalidRequest, 'invalid_request');
  if (status === 429) return new InsightsError(MESSAGES.busy, 'busy');
  return new InsightsError(MESSAGES.failed, 'failed');
}

/**
 * Combine the caller's signal with a 300 s timeout.
 * `signal` aborts when either fires; `timedOut()` tells them apart.
 */
function createRequestSignal(callerSignal?: AbortSignal) {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, INSIGHTS_TIMEOUT_MS);

  const onCallerAbort = () => controller.abort();
  if (callerSignal?.aborted) {
    controller.abort();
  } else {
    callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  }

  return {
    signal: controller.signal,
    timedOut: () => timedOut && !callerSignal?.aborted,
    callerAborted: () => callerSignal?.aborted === true,
    cleanup: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

type RawEvent = Record<string, unknown>;

/** Parse a single SSE `data: ` line. Returns null for anything unparseable. */
function parseSSEEvent(line: string): RawEvent | null {
  const prefix = 'data: ';
  if (!line.startsWith(prefix)) return null;

  const jsonStr = line.slice(prefix.length).trim();
  if (!jsonStr) return null;

  try {
    const parsed: unknown = JSON.parse(jsonStr);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as RawEvent;
    }
    return null;
  } catch {
    return null;
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Build an Activity from an `activity` event, or null to drop it.
 * Same resilience as chat: any missing required field drops the event. The
 * activity type is passed through untouched so unknown types render generically.
 */
function toActivity(event: RawEvent): Activity | null {
  const { activity_id, activity_type, status, label, ts, detail } = event;
  if (
    !isNonEmptyString(activity_id) ||
    !isNonEmptyString(activity_type) ||
    !isNonEmptyString(label) ||
    !isNonEmptyString(ts) ||
    !isNonEmptyString(status) ||
    !VALID_ACTIVITY_STATUSES.includes(status)
  ) {
    return null;
  }
  return {
    id: activity_id,
    type: activity_type,
    status: status as ActivityStatus,
    label,
    ts,
    detail:
      detail && typeof detail === 'object' ? (detail as Activity['detail']) : undefined,
  };
}

function isReportObject(value: unknown): value is InsightsReport {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Sanitized server error message, or the generic failure text. */
function serverMessage(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim().slice(0, MAX_SERVER_MESSAGE_LENGTH);
  }
  return MESSAGES.failed;
}

/**
 * Generate a fresh Insights_Report for an account and stream its progress.
 *
 * Resolves once the request has finished. Failures are reported through
 * `onError` (never thrown). Exactly one of `onComplete` / `onError` is invoked,
 * except when the caller aborts, in which case neither is.
 *
 * The report is held until the terminal `done` event, so a stream that fails
 * part-way never surfaces a partial report (Requirement 7.5).
 *
 * @param accountId - Account to analyze
 * @param token - Cognito JWT for the Authorization header
 * @param handlers - Event callbacks and optional AbortSignal
 */
export async function generateInsights(
  accountId: string,
  token: string,
  handlers: GenerateInsightsHandlers
): Promise<void> {
  const { onActivity, onReport, onComplete, onError, signal } = handlers;

  let settled = false;
  const fail = (error: InsightsError) => {
    if (settled) return;
    settled = true;
    onError(error);
  };

  let url: string;
  try {
    url = resolveInsightsUrl();
  } catch (error) {
    fail(error as InsightsError);
    return;
  }

  if (!accountId.trim()) {
    fail(new InsightsError(MESSAGES.invalidRequest, 'invalid_request'));
    return;
  }

  if (signal?.aborted) return;

  const request = createRequestSignal(signal);
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let report: InsightsReport | null = null;

  /** Apply one SSE data line. */
  const handleLine = (line: string) => {
    if (settled) return;
    const event = parseSSEEvent(line);
    if (!event) return;

    switch (event.type) {
      case 'activity': {
        // Additive and isolated: never throw, never block the report.
        const activity = toActivity(event);
        if (activity) {
          try {
            onActivity?.(activity);
          } catch {
            // A failing activity consumer must not affect report delivery.
          }
        }
        break;
      }

      case 'insights_report':
        if (isReportObject(event.report)) {
          report = event.report;
        }
        break;

      case 'done':
        if (report) {
          settled = true;
          onReport(report);
          onComplete();
        } else {
          fail(new InsightsError(MESSAGES.failed, 'failed'));
        }
        break;

      case 'error':
        report = null;
        fail(new InsightsError(serverMessage(event.message), 'failed'));
        break;
    }
  };

  /** Report a failure that happened while the request was aborted or broken. */
  const failFromInterruption = () => {
    if (request.callerAborted()) return;
    if (request.timedOut()) {
      fail(new InsightsError(MESSAGES.timeout, 'timeout'));
    } else {
      fail(new InsightsError(MESSAGES.failed, 'failed'));
    }
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ account_id: accountId }),
      signal: request.signal,
    });

    if (!response.ok) {
      fail(errorForStatus(response.status));
      return;
    }
    if (!response.body) {
      fail(new InsightsError(MESSAGES.failed, 'failed'));
      return;
    }

    const activeReader = response.body.getReader();
    reader = activeReader;
    const cancelReader = () => {
      activeReader.cancel().catch(() => undefined);
    };
    if (request.signal.aborted) {
      cancelReader();
    } else {
      request.signal.addEventListener('abort', cancelReader, { once: true });
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (!settled) {
      const { done, value } = await activeReader.read();

      if (done) {
        buffer += decoder.decode();
        // A buffered body may end without a trailing blank line.
        for (const line of buffer.split('\n')) handleLine(line);
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by blank lines; keep the incomplete tail.
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        for (const line of part.split('\n')) handleLine(line);
        if (settled) break;
      }
    }

    if (!settled) {
      // Stream ended without a terminal event (aborted, timed out, or dropped).
      failFromInterruption();
    }
  } catch {
    // Network failure, abort, or a malformed stream.
    failFromInterruption();
  } finally {
    request.cleanup();
    try {
      reader?.releaseLock();
    } catch {
      // Lock cannot be released while a cancel is pending; safe to ignore.
    }
  }
}

/**
 * Fetch the most recent stored Insights_Report for an account.
 *
 * @returns `{ report }` with the stored report, or `{ report: null }` when none exists
 * @throws InsightsError on configuration problems, non-2xx responses, timeout,
 *         network failure, or an unreadable response, so the caller can offer a retry
 */
export async function fetchLatestInsights(
  accountId: string,
  token: string,
  signal?: AbortSignal
): Promise<LatestInsightsResponse> {
  const baseUrl = resolveInsightsUrl();
  if (!accountId.trim()) {
    throw new InsightsError(MESSAGES.invalidRequest, 'invalid_request');
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  const url = `${baseUrl}${separator}account_id=${encodeURIComponent(accountId)}`;

  const request = createRequestSignal(signal);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: request.signal,
    });

    if (!response.ok) {
      throw errorForStatus(response.status);
    }

    const body: unknown = await response.json();
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      throw new InsightsError(MESSAGES.loadFailed, 'failed');
    }
    const report = (body as { report?: unknown }).report ?? null;
    if (report !== null && !isReportObject(report)) {
      throw new InsightsError(MESSAGES.loadFailed, 'failed');
    }
    return { report };
  } catch (error) {
    if (error instanceof InsightsError) throw error;
    // A caller abort propagates as the original AbortError so it is not shown as a failure.
    if (request.callerAborted()) throw error;
    if (request.timedOut()) throw new InsightsError(MESSAGES.timeout, 'timeout');
    throw new InsightsError(MESSAGES.loadFailed, 'failed');
  } finally {
    request.cleanup();
  }
}
